/**
 * frontend/src/components/layout/SearchModal.tsx
 * PHASE 3.2 F2 — Modal Cmd+K recherche globale.
 *
 * Fonctionnalités :
 *   - Ouverture Cmd+K (Mac) / Ctrl+K (Windows/Linux) via KeyboardEvent global
 *   - Recherche debounced 300ms — tsvector backend (PDVs, marques, clusters, modules)
 *   - Navigation au clavier (CommandItem) vers la route correspondante
 *   - Icônes par type de résultat
 */

import { dire } from '@/lib/mots';
import { useT } from '@/lib/i18n';
import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Store, Tag, MapPin, LayoutDashboard, Smartphone } from 'lucide-react'
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
  CommandLoading,
} from '@/components/ui/command'
import { useSearch } from '@/modules/search/hooks/useSearch'
import type { SearchResultItem, SearchResultType } from '@/modules/search/types'
import { RESULT_TYPE_MOTS } from '@/modules/search/types'
import { cn } from '@/lib/utils'

// ── Icon per type ────────────────────────────────────────────────────────────────
function ResultIcon({ type, className }: { type: SearchResultType; className?: string }) {
  const cls = cn('h-3.5 w-3.5 shrink-0', className)
  switch (type) {
    case 'pdv':            return <Store className={cls} />
    case 'brand':          return <Tag className={cls} />
    case 'market_cluster': return <MapPin className={cls} />
    case 'module':         return <LayoutDashboard className={cls} />
    case 'sku':            return <Smartphone className={cls} />
  }
}

// ── Type badge ───────────────────────────────────────────────────────────────────
const TYPE_BADGE_CLASSES: Record<SearchResultType, string> = {
  pdv:            'bg-blue-500/10 text-blue-700 dark:text-blue-400',
  brand:          'bg-violet-500/10 text-violet-700 dark:text-violet-400',
  market_cluster: 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
  module:         'bg-brand-samsung-100 dark:bg-brand-samsung-500/10 text-brand-samsung-700 dark:text-brand-samsung-300',
  sku:            'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
}

// ── Group order ───────────────────────────────────────────────────────────────────
const GROUP_ORDER: SearchResultType[] = ['module', 'sku', 'pdv', 'brand', 'market_cluster']

interface SearchModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

// Debounce helper
function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    // ⚠⚠ `#829` — ce `t` masquait le traducteur : renommé AVANT que `useT`
    //   n'entre dans le fichier. La collision était structurelle.
    const minuterie = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(minuterie)
  }, [value, delay])
  return debounced
}

export function SearchModal({ open, onOpenChange }: SearchModalProps) {
  const t = useT();
  const navigate = useNavigate()
  const [inputValue, setInputValue] = useState('')
  const debouncedQuery = useDebounced(inputValue, 300)
  const inputRef = useRef<HTMLInputElement>(null)

  const { data, isFetching } = useSearch(debouncedQuery)

  // Reset input on close
  useEffect(() => {
    if (!open) setInputValue('')
  }, [open])

  // Group results by type
  const grouped = GROUP_ORDER.reduce<Record<SearchResultType, SearchResultItem[]>>(
    (acc, type) => {
      acc[type] = data?.results.filter((r) => r.type === type) ?? []
      return acc
    },
    { module: [], sku: [], pdv: [], brand: [], market_cluster: [] },
  )

  const handleSelect = useCallback(
    (item: SearchResultItem) => {
      onOpenChange(false)
      if (item.type === 'module') {
        navigate(item.code)
      } else if (item.type === 'sku') {
        navigate(`/referentiels/sku?search=${encodeURIComponent(item.name)}`)
      } else if (item.type === 'pdv') {
        navigate(`/referentiels/pdv?search=${encodeURIComponent(item.name)}`)
      } else if (item.type === 'brand') {
        navigate(`/referentiels/brands?search=${encodeURIComponent(item.name)}`)
      }
      // `#1068` — Market Cluster masqué : plus de navigation depuis la recherche
      //   (l'écran n'est plus atteignable). Les résultats éventuels ne mènent nulle part.
    },
    [navigate, onOpenChange],
  )

  const isEmpty =
    !isFetching &&
    debouncedQuery.length > 0 &&
    (data?.total ?? 0) === 0

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        ref={inputRef}
        placeholder={t('commun.rechercherQuoi')}
        value={inputValue}
        onValueChange={setInputValue}
        data-testid="search-input"
      />

      <CommandList data-testid="search-results-list">
        {isFetching && debouncedQuery.length > 0 && (
          <CommandLoading>{t('commun.rechercheEnCours')}</CommandLoading>
        )}

        {isEmpty && (
          <CommandEmpty data-testid="search-empty">
            {t('commun.aucunResultatPour', { recherche: debouncedQuery })}
          </CommandEmpty>
        )}

        {/* Default shortcuts when no query */}
        {inputValue.length === 0 && (
          <CommandGroup heading={t('commun.accesRapide')}>
            {grouped.module.length > 0
              ? grouped.module.slice(0, 6).map((item) => (
                  <ResultItem key={item.id} item={item} onSelect={handleSelect} />
                ))
              : [
                  { id: 'module-alerts', type: 'module' as const, code: '/welcome', name: t('nav.tableauDeBord'), rank: 1 },
                  { id: 'module-marche', type: 'module' as const, code: '/marche', name: t('nav.marche'), rank: 1 },
                  { id: 'module-performance', type: 'module' as const, code: '/performance', name: t('nav.performance'), rank: 1 },
                ].map((item) => (
                  <ResultItem key={item.id} item={item} onSelect={handleSelect} />
                ))
            }
          </CommandGroup>
        )}

        {/* Search results */}
        {inputValue.length > 0 && !isFetching && (
          <>
            {grouped.module.length > 0 && (
              <CommandGroup heading={dire(RESULT_TYPE_MOTS.module, t)}>
                {grouped.module.map((item) => (
                  <ResultItem key={item.id} item={item} onSelect={handleSelect} />
                ))}
              </CommandGroup>
            )}

            {grouped.sku.length > 0 && (
              <>
                {grouped.module.length > 0 && <CommandSeparator />}
                <CommandGroup heading={dire(RESULT_TYPE_MOTS.sku, t)}>
                  {grouped.sku.map((item) => (
                    <ResultItem key={item.id} item={item} onSelect={handleSelect} />
                  ))}
                </CommandGroup>
              </>
            )}

            {grouped.pdv.length > 0 && (
              <>
                {(grouped.module.length > 0 || grouped.sku.length > 0) && <CommandSeparator />}
                <CommandGroup heading={dire(RESULT_TYPE_MOTS.pdv, t)}>
                  {grouped.pdv.map((item) => (
                    <ResultItem key={item.id} item={item} onSelect={handleSelect} />
                  ))}
                </CommandGroup>
              </>
            )}

            {grouped.brand.length > 0 && (
              <>
                {(grouped.module.length > 0 || grouped.sku.length > 0 || grouped.pdv.length > 0) && <CommandSeparator />}
                <CommandGroup heading={dire(RESULT_TYPE_MOTS.brand, t)}>
                  {grouped.brand.map((item) => (
                    <ResultItem key={item.id} item={item} onSelect={handleSelect} />
                  ))}
                </CommandGroup>
              </>
            )}

            {grouped.market_cluster.length > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup heading={dire(RESULT_TYPE_MOTS.market_cluster, t)}>
                  {grouped.market_cluster.map((item) => (
                    <ResultItem key={item.id} item={item} onSelect={handleSelect} />
                  ))}
                </CommandGroup>
              </>
            )}
          </>
        )}
      </CommandList>

      {/* Footer */}
      <div className="border-t border-[var(--border-default)] px-3 py-2 flex items-center gap-3 text-[10px] text-muted-foreground/60">
        <span><kbd className="rounded bg-[var(--surface-hover)] px-1 py-px font-mono">↑↓</kbd> {t('commun.naviguer')}</span>
        <span><kbd className="rounded bg-[var(--surface-hover)] px-1 py-px font-mono">↵</kbd> {t('commun.selectionnerTouche')}</span>
        <span><kbd className="rounded bg-[var(--surface-hover)] px-1 py-px font-mono">{t('commun.toucheEchap')}</kbd> {t('commun.fermerMinuscule')}</span>
      </div>
    </CommandDialog>
  )
}

// ── Result item ──────────────────────────────────────────────────────────────────
function ResultItem({
  item,
  onSelect,
}: {
  item: SearchResultItem
  onSelect: (item: SearchResultItem) => void
}) {
  return (
    <CommandItem
      key={item.id}
      value={`${item.type}-${item.id}-${item.name}`}
      onSelect={() => onSelect(item)}
      data-testid={`search-result-${item.type}-${item.id}`}
      className="flex items-center gap-2.5"
    >
      <span className={cn('rounded-md p-1', TYPE_BADGE_CLASSES[item.type])}>
        <ResultIcon type={item.type} />
      </span>
      <span className="flex-1 min-w-0">
        <span className="block truncate text-sm font-medium text-foreground">
          {item.name}
        </span>
        {item.meta && (
          <span className="block truncate text-[11px] text-muted-foreground">
            {item.meta}
          </span>
        )}
      </span>
      <span className="shrink-0 text-[10px] font-mono text-muted-foreground/50">
        {item.code}
      </span>
    </CommandItem>
  )
}
